import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setUser } from './store/authSlice';
import { authService } from './services/auth';

// Layout Components
import Header from './components/common/Header';
import Sidebar from './components/common/Sidebar';
import ErrorBoundary from './components/common/ErrorBoundary';
import SuspenseLoader from './components/common/SuspenseLoader';

// Auth Components
import ProtectedRoute from './components/auth/ProtectedRoute';

// Pages - Direct imports for auth pages (critical)
import Login from './pages/Login';
// import Register from './pages/Register'; // Disabled - user creation removed
import Dashboard from './pages/Dashboard';
import TestDashboard from './pages/TestDashboard';

// Lazy loaded pages for better performance
const Models = React.lazy(() => import('./pages/Models'));
const ModelDetail = React.lazy(() => import('./pages/ModelDetail'));
const Codes = React.lazy(() => import('./pages/Codes'));
const EditModel = React.lazy(() => import('./pages/EditModel'));
const EditCode = React.lazy(() => import('./pages/EditCode'));
const Admin = React.lazy(() => import('./pages/Admin'));
const ImportExport = React.lazy(() => import('./pages/ImportExport'));

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    // Initialize user data from token if available
    const currentUser = authService.getCurrentUser();
    if (currentUser && !user) {
      dispatch(setUser(currentUser));
    }
  }, [dispatch, user]);

  return (
    <ErrorBoundary>
      <div className="h-screen bg-gray-50 flex overflow-hidden">
        {/* Sidebar */}
        {isAuthenticated && (
          <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        )}

        {/* Main content */}
        <div className="flex flex-col w-0 flex-1 overflow-hidden">
          {/* Header */}
          {isAuthenticated && (
            <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
          )}

          {/* Main content area */}
          <main className="flex-1 relative overflow-y-auto focus:outline-none">
            <div className={`${isAuthenticated ? 'py-6 px-4 sm:px-6 lg:px-8' : ''}`}>
              <Suspense fallback={<SuspenseLoader />}>
                <Routes>
                  {/* Public Routes */}
                  <Route 
                    path="/login" 
                    element={
                      isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
                    } 
                  />
                  {/* Register route disabled - user creation removed from login page
                  <Route 
                    path="/register" 
                    element={
                      isAuthenticated ? <Navigate to="/dashboard" replace /> : <Register />
                    } 
                  />
                  */}

                  {/* Protected Routes */}
                  <Route 
                    path="/dashboard" 
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    } 
                  />
                  
                  <Route 
                    path="/models" 
                    element={
                      <ProtectedRoute>
                        <Models />
                      </ProtectedRoute>
                    } 
                  />
                  
                  <Route 
                    path="/models/:code" 
                    element={
                      <ProtectedRoute>
                        <ModelDetail />
                      </ProtectedRoute>
                    } 
                  />
                  
                  <Route 
                    path="/models/:code/edit" 
                    element={
                      <ProtectedRoute adminOnly>
                        <EditModel />
                      </ProtectedRoute>
                    } 
                  />
                  
                  <Route 
                    path="/codes" 
                    element={
                      <ProtectedRoute>
                        <Codes />
                      </ProtectedRoute>
                    } 
                  />
                  
                  <Route 
                    path="/codes/:id/edit" 
                    element={
                      <ProtectedRoute adminOnly>
                        <EditCode />
                      </ProtectedRoute>
                    } 
                  />
                  
                  <Route 
                    path="/import-export" 
                    element={
                      <ProtectedRoute>
                        <ImportExport />
                      </ProtectedRoute>
                    } 
                  />
                  
                  <Route 
                    path="/admin" 
                    element={
                      <ProtectedRoute adminOnly>
                        <Admin />
                      </ProtectedRoute>
                    } 
                  />

                  <Route 
                    path="/test" 
                    element={<TestDashboard />} 
                  />

                  {/* Default redirect */}
                  <Route 
                    path="/" 
                    element={
                      <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
                    } 
                  />
                  
                  {/* Catch all route */}
                  <Route 
                    path="*" 
                    element={
                      <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
                    } 
                  />
                </Routes>
              </Suspense>
            </div>
          </main>
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default App;
